import React, { useEffect, useState } from 'react';
import { string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { twitterPageURL } from '../../util/urlHelpers';
import config from '../../config';
import {
  IconSocialMediaFacebook,
  IconSocialMediaInstagram,
  IconSocialMediaTwitter,
  Logo,
  ExternalLink,
  NamedLink,
  IconCollection,
} from '../../components';

import css from './Footer.module.css';
import appLogoImage from '../../assets/app-logo.png';
import { ALL_GENDER } from '../../util/types';
import StarRatings from 'react-star-ratings';

const StaticReviews = () => {
  const [rating, setRating] = useState(null);
  const [ratingsCount, setRatingsCount] = useState(null);

  useEffect(() => {
    fetch('https://itunes.apple.com/lookup?id=1632635913')
      .then(res => res.json())
      .then(data => {
        if (data.resultCount > 0) {
          const app = data.results[0];
          setRating(app.averageUserRating);
          setRatingsCount(app.userRatingCount);
        }
      })
      .catch(err => console.error('Failed to fetch app rating', err));
  }, []);

  if (!rating) return null; // or a loader/spinner

  return (
    <div className={css.staticReviews}>
      <StarRatings
        svgIconViewBox="0 0 40 37"
        svgIconPath="M20 0L26.113 11.5862L39.0211 13.8197L29.891 23.2138L31.7557 36.1803L20 30.4L8.2443 36.1803L10.109 23.2138L0.97887 13.8197L13.887 11.5862L20 0Z"
        starRatedColor="#ffb802"
        rating={rating}
        starDimension="20px"
        starSpacing="2px"
      />
      {/* &nbsp;{ratingsCount ? <span>({ratingsCount.toLocaleString()})</span> : null} */}
      &nbsp;{ratingsCount ? <span>(2.9k+)</span> : null}
    </div>
  );
};

export const AppDownloadPrompt = ({ isFooter = false }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [platform, setPlatform] = useState(null);
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const iosUrl = 'https://apps.apple.com/us/app/buysell-buy-sell-clothes/id1632635913';
  const androidUrl = 'https://play.google.com/store/apps/details?id=com.clothing.buysell&pli=1';

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    const mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent);
    setIsMobile(mobile);

    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      setPlatform('ios');
    } else if (/Android/i.test(userAgent)) {
      setPlatform('android');
    }
  }, []);

  const handleAppClick = platform => {
    console.log(`Tracking Event: app_download_clicked (${platform})`);
  };

  const handleCloseBanner = () => {
    setIsBannerVisible(false);
  };

  return (
    <div className={css.appPrompt}>
      {isMobile && !isFooter && isBannerVisible ? (
        <div className={css.appBanner}>
          {platform === 'ios' && (
            <a href={iosUrl} className={css.appStoreLink} onClick={() => handleAppClick('ios')}>
              <img src={appLogoImage} alt="BuySell Clothing App" className={css.appLogo} />
            </a>
          )}
          {platform === 'android' && (
            <a
              href={androidUrl}
              className={css.appStoreLink}
              onClick={() => handleAppClick('android')}
            >
              <img src={appLogoImage} alt="BuySell Clothing App" className={css.appLogo} />
            </a>
          )}

          <div>
            <p className={css.appBannerTitle}>BuySell Clothing App</p>
            <p className={css.appBannerText}>Download our App for a better experience!</p>
            {/* <StaticReviews /> */}
          </div>
          <div className={css.appBannerActions}>
            {platform === 'ios' && (
              <a href={iosUrl} className={css.appStoreLink} onClick={() => handleAppClick('ios')}>
                <IconCollection icon="appleIcon" />
              </a>
            )}
            {platform === 'android' && (
              <a
                href={androidUrl}
                className={css.appStoreLink}
                onClick={() => handleAppClick('android')}
              >
                <IconCollection icon="androidIcon" />
              </a>
            )}
          </div>
          <button
            className={css.closeButton}
            onClick={handleCloseBanner}
            aria-label="Close app banner"
          >
            ×
          </button>
        </div>
      ) : !isMobile ? (
        <div className={css.qrSection}>
          <p className={css.qrTitle}>BuySell Clothing App</p>
          <p className={css.qrSubtitle}>Scan and download our App for a better experience!</p>
          <div className={css.qrGrid}>
            <div className={css.qrCard}>
              <IconCollection icon="app_qr" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const renderSocialMediaLinks = intl => {
  const { siteFacebookPage, siteInstagramPage, siteTwitterHandle } = config;
  const siteTwitterPage = twitterPageURL(siteTwitterHandle);

  const goToFb = intl.formatMessage({ id: 'Footer.goToFacebook' });
  const goToInsta = intl.formatMessage({ id: 'Footer.goToInstagram' });
  const goToTwitter = intl.formatMessage({ id: 'Footer.goToTwitter' });

  const fbLink = siteFacebookPage ? (
    <ExternalLink key="linkToFacebook" href={siteFacebookPage} className={css.icon} title={goToFb}>
      <IconSocialMediaFacebook />
    </ExternalLink>
  ) : null;

  const twitterLink = siteTwitterPage ? (
    <ExternalLink
      key="linkToTwitter"
      href={siteTwitterPage}
      className={css.icon}
      title={goToTwitter}
    >
      <IconSocialMediaTwitter />
    </ExternalLink>
  ) : null;

  const instragramLink = siteInstagramPage ? (
    <ExternalLink
      key="linkToInstagram"
      href={siteInstagramPage}
      className={css.icon}
      title={goToInsta}
    >
      <IconSocialMediaInstagram />
    </ExternalLink>
  ) : null;
  return [fbLink, twitterLink, instragramLink].filter(v => v != null);
};

const Footer = props => {
  const { rootClassName, className, intl, ColorVariant, CategorySelected, isLanding } = props;
  const socialMediaLinks = renderSocialMediaLinks(intl);
  const classes = classNames(
    rootClassName || css.root,
    className,
    ColorVariant !== 'white' ? css.footerDark : css.footerWhite
  );

  return (
    <div className={classes}>
      <div className={css.topBorderWrapper}>
        <div className={css.content}>
          <div className={css.someLiksMobile}>{socialMediaLinks}</div>
          <div className={css.links}>
            <div className={css.organization} id="organization">
              <NamedLink
                name="LandingPageListingStore"
                params={{ type: 'all', gender: ALL_GENDER }}
                className={css.logoLink}
              >
                <Logo
                  format={ColorVariant !== 'white' ? 'footer' : 'desktop'}
                  className={css.logo}
                  CategorySelected={CategorySelected}
                  isLanding={isLanding}
                />
              </NamedLink>
              <div className={css.organizationInfo}>
                <p className={css.organizationDescription}>
                  <FormattedMessage id="Footer.marketplaceDescription" />
                </p>
                <p className={css.organizationCopyright}>
                  <NamedLink
                    name="LandingPageListingStore"
                    params={{ type: 'all', gender: ALL_GENDER }}
                    className={css.copyrightLink}
                  >
                    <FormattedMessage id="Footer.copyright" />
                  </NamedLink>
                </p>
              </div>
            </div>
            <div className={css.infoLinks}>
              <ul className={css.list}>
                <li className={css.listItem}>
                  <NamedLink name="NewListingPage" rel="nofollow" className={css.link}>
                    <FormattedMessage id="Footer.toNewListingPage" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink name="CMSPage" params={{ pageId: 'about' }} className={css.link}>
                    <FormattedMessage id="Footer.toAboutPage" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink name="CMSPage" params={{ pageId: 'faq' }} className={css.link}>
                    <FormattedMessage id="Footer.toFAQPage" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="LandingPageListingStore"
                    params={{ type: 'all', gender: ALL_GENDER }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.toHelpPage" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="CMSPage"
                    params={{ pageId: 'contact-us' }}
                    // to={{ hash: '#contact' }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.toContactPage" />
                  </NamedLink>
                </li>
              </ul>
            </div>
            <div className={css.searches}>
              <ul className={css.list}>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=adidas',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link1" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=air_jordan',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link2" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=converse',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link3" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=new_balance',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link4" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=nike',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link5" />
                  </NamedLink>
                </li>
              </ul>
            </div>
            <div className={css.searchesExtra}>
              <ul className={css.list}>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=puma',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link6" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=ultraboost',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link7" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=vans',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link8" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=yeezy',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link9" />
                  </NamedLink>
                </li>
                <li className={css.listItem}>
                  <NamedLink
                    name="SearchPage"
                    to={{
                      search: '?pub_brand=other',
                    }}
                    className={css.link}
                  >
                    <FormattedMessage id="Footer.link10" />
                  </NamedLink>
                </li>
              </ul>
            </div>
            <div className={css.extraLinks}>
              <div className={css.someLinks}>{socialMediaLinks}</div>
              <div className={css.legalMatters}>
                <ul className={css.tosAndPrivacy}>
                  <li>
                    <NamedLink name="TermsOfServicePage" className={css.legalLink}>
                      <FormattedMessage id="Footer.termsOfUse" />
                    </NamedLink>
                  </li>
                  <li>
                    <NamedLink name="PrivacyPolicyPage" className={css.legalLink}>
                      <FormattedMessage id="Footer.privacyPolicy" />
                    </NamedLink>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className={css.copyrightAndTermsMobile}>
            <NamedLink
              name="LandingPageListingStore"
              params={{ type: 'all', gender: ALL_GENDER }}
              className={css.organizationCopyrightMobile}
            >
              <FormattedMessage id="Footer.copyright" />
            </NamedLink>
            <div className={css.tosAndPrivacyMobile}>
              <NamedLink name="PrivacyPolicyPage" className={css.privacy}>
                <FormattedMessage id="Footer.privacy" />
              </NamedLink>
              <NamedLink name="TermsOfServicePage" className={css.terms}>
                <FormattedMessage id="Footer.terms" />
              </NamedLink>
            </div>
          </div>
          <AppDownloadPrompt />
        </div>
      </div>
    </div>
  );
};

Footer.defaultProps = {
  rootClassName: null,
  className: null,
};

Footer.propTypes = {
  rootClassName: string,
  className: string,
  intl: intlShape.isRequired,
};

export default injectIntl(Footer);
